import { SpanStatusCode, trace } from '@opentelemetry/api'
import { ATTR_GECKO_SITE_ID } from 'features/honeycomb/spanAttributes'

import {
  ATTR_GECKO_PAGE_IMAGE_DOCUMENT_ID,
  ATTR_GECKO_PAGE_IMAGE_IS_CURRENT_PAGE,
  ATTR_GECKO_PAGE_IMAGE_OUTCOME,
  ATTR_GECKO_PAGE_IMAGE_PAGE_NUMBER,
  ATTR_GECKO_PAGE_IMAGE_REASON,
  ATTR_GECKO_PAGE_IMAGE_RETRIED,
  ATTR_GECKO_PAGE_IMAGE_SOURCE,
  ATTR_GECKO_PAGE_IMAGE_SURFACE,
} from './pageImageSpanAttributes'

export const PAGE_IMAGE_DISPLAYED_SPAN = 'page_image.displayed'

export type PageImageSurface = 'continuous' | 'paged'
export type PageImageSource = 'uuid5' | 'legacy' | 'pdf'
export type PageImageFailureReason =
  | 'lookup_unavailable'
  | 'no_url'
  | 'image_error'
  | 'pdf_render_failed'

export type PageImageResult =
  | { outcome: 'displayed'; source: PageImageSource }
  | {
      outcome: 'failed'
      reason: PageImageFailureReason
      source?: PageImageSource
    }

export type PageImageOutcomeReport = {
  siteId: string | undefined
  documentId: string
  pageNumber: number
  surface: PageImageSurface
  isCurrentPage: boolean
  retried: boolean
  expectedAt: number
  result: PageImageResult
}

/**
 * One span per page image the viewer expected to show. The span starts when
 * the page slot asked for an image and ends when it was painted or given up on,
 * so its duration is the time to display.
 */
export function reportPageImageOutcome(report: PageImageOutcomeReport): void {
  const { result } = report
  const span = trace
    .getTracer('forecast')
    .startSpan(PAGE_IMAGE_DISPLAYED_SPAN, {
      startTime: report.expectedAt,
      attributes: {
        [ATTR_GECKO_PAGE_IMAGE_DOCUMENT_ID]: report.documentId,
        [ATTR_GECKO_PAGE_IMAGE_PAGE_NUMBER]: report.pageNumber,
        [ATTR_GECKO_PAGE_IMAGE_SURFACE]: report.surface,
        [ATTR_GECKO_PAGE_IMAGE_IS_CURRENT_PAGE]: report.isCurrentPage,
        [ATTR_GECKO_PAGE_IMAGE_RETRIED]: report.retried,
        [ATTR_GECKO_PAGE_IMAGE_OUTCOME]: result.outcome,
        ...(report.siteId ? { [ATTR_GECKO_SITE_ID]: report.siteId } : {}),
        ...(result.source
          ? { [ATTR_GECKO_PAGE_IMAGE_SOURCE]: result.source }
          : {}),
        ...(result.outcome === 'failed'
          ? { [ATTR_GECKO_PAGE_IMAGE_REASON]: result.reason }
          : {}),
      },
    })
  if (result.outcome === 'failed') {
    span.setStatus({ code: SpanStatusCode.ERROR, message: result.reason })
  }
  span.end()
}

export type PageImageFailureSignals = {
  exhausted: boolean
  pdfBroken: boolean
  pdfError: boolean
  lookupUnavailable: boolean
  pdfAttempted: boolean
}

/** Names the first thing that went wrong for a page image and the last source that was tried. */
export function resolvePageImageFailure(signals: PageImageFailureSignals): {
  reason: PageImageFailureReason
  source: PageImageSource | undefined
} {
  const reason: PageImageFailureReason = signals.exhausted
    ? 'image_error'
    : signals.pdfBroken || signals.pdfError
      ? 'pdf_render_failed'
      : signals.lookupUnavailable
        ? 'lookup_unavailable'
        : 'no_url'
  return {
    reason,
    source: signals.pdfAttempted || signals.pdfBroken ? 'pdf' : undefined,
  }
}
